import {
  isButtonRow,
  isTableWrapperRow,
  parseRowBackground,
  parseRowStyleAttr,
} from "../html-parse";
import {
  EMPTY_STYLE_MAP,
  applyStyleMap,
  paintStyles,
} from "../shared-block-styles";
import { Node } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

import type { CSSProperties } from "react";

import { shrinksToFit } from "./sizing";

const STRAY_LIST_TYPES = new Set(["bulletList", "orderedList"]);

// Here rather than in normalizeEmailContent, which only sees initialContent.
const wrapStrayListsInRows = new Plugin({
  key: new PluginKey("wrapStrayListsInRows"),
  appendTransaction(_transactions, _oldState, newState) {
    const strays: { pos: number; size: number }[] = [];
    newState.doc.forEach((node, offset) => {
      if (STRAY_LIST_TYPES.has(node.type.name)) {
        strays.push({ pos: offset, size: node.nodeSize });
      }
    });
    if (strays.length === 0) return null;

    const rowType = newState.schema.nodes["emailRow"];
    const cellType = newState.schema.nodes["emailCell"];
    if (!rowType || !cellType) return null;

    const { tr } = newState;
    // Back to front so each wrap leaves the earlier positions untouched.
    for (let i = strays.length - 1; i >= 0; i--) {
      const { pos, size } = strays[i];
      const list = newState.doc.nodeAt(pos);
      if (!list) continue;
      tr.replaceWith(
        pos,
        pos + size,
        rowType.create(null, [cellType.create({ colSpan: 4 }, [list])]),
      );
    }
    return tr;
  },
});

/**
 * emailRow node — a structural container that holds 1-4 emailCell nodes.
 * Rendered as CSS `display: table` in the editor to match the email output's
 * `<table>/<tr>` structure for true WYSIWYG.
 */
export const EmailRow = Node.create({
  name: "emailRow",
  group: "emailRow",
  content: "emailCell+",
  isolating: true,
  defining: true,

  addAttributes() {
    return {
      /**
       * The colour band behind a row. Designed emails paint it on the layout
       * table, which has no node of its own here — without somewhere to store
       * it the band was simply lost on load.
       */
      backgroundColor: {
        default: "",
        parseHTML: (element: HTMLElement) => {
          const stored = element.getAttribute("data-background-color");
          if (stored !== null) return stored;
          return parseRowBackground(element);
        },
      },
      /** The row's inline styles, as written — plus its table's own box. */
      style: {
        default: EMPTY_STYLE_MAP,
        parseHTML: (element: HTMLElement) => parseRowStyleAttr(element),
        // Carried in the node's JSON, never as an HTML attribute — the
        // element's own `style` is built from the grid's own values below.
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    // `tr` is how a designed email spells a row. The wrapping `table`/`tbody`
    // need no rule of their own — ProseMirror walks through unmatched elements
    // and keeps parsing their children, so the rows inside still land.
    return [
      { tag: 'div[data-row="true"]', priority: 100 },
      {
        tag: "tr",
        // Declining leaves the element unmatched, and ProseMirror then parses
        // its children in place — which is exactly what a layout wrapper or a
        // button frame should do: contribute its contents, not a row.
        getAttrs: (node) => {
          const el = node as HTMLElement;
          if (isTableWrapperRow(el) || isButtonRow(el)) return false;
          return null;
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const backgroundColor = node.attrs.backgroundColor as string;
    return [
      "div",
      {
        ...HTMLAttributes,
        "data-row": "true",
        ...(backgroundColor
          ? { "data-background-color": backgroundColor }
          : {}),
      },
      0,
    ];
  },

  addProseMirrorPlugins() {
    return [wrapStrayListsInRows];
  },

  addNodeView() {
    return ({ node: initialNode, HTMLAttributes }) => {
      let node = initialNode;
      const dom = document.createElement("div");
      dom.setAttribute("data-row", "true");
      dom.className = "email-row";

      // Apply any extra attributes
      for (const [key, value] of Object.entries(HTMLAttributes)) {
        if (typeof value === "string") {
          dom.setAttribute(key, value);
        }
      }

      // Paint the design in the editor too, so the composer shows what the
      // inbox will. The row's own declarations go on first and the band on
      // top, the same order the send renderer uses — otherwise a border folded
      // on from a one-row table shows in the email and not in the composer,
      // and the two disagree about the same document.
      let painted = new Set<string>();
      const applyDesign = (attrs: Record<string, unknown>) => {
        const styles: CSSProperties = {};
        applyStyleMap(styles, attrs);
        // A row that sizes itself — a date chip, a button's frame — has to be
        // allowed to shrink, or the auto margin meant to centre it has nothing
        // to centre and it spans the whole width instead.
        styles.width = shrinksToFit(styles)
          ? (styles.width as string) || "fit-content"
          : "";
        const backgroundColor = attrs.backgroundColor as string;
        if (backgroundColor) styles.backgroundColor = backgroundColor;
        painted = paintStyles(dom, styles, painted);
      };
      applyDesign(node.attrs);

      const contentDOM = dom;
      return {
        dom,
        contentDOM,
        update(updatedNode) {
          if (updatedNode.type.name !== "emailRow") return false;
          applyDesign(updatedNode.attrs);
          node = updatedNode;
          return true;
        },
      };
    };
  },
});
